import type { ComponentPropsWithoutRef } from 'react';
import { LoaderCircle, Volume2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useSpeechSynthesis } from '@/hooks/useSpeechSynthesis';

const SPEECH_LOCALES: Record<string, string> = {
  en: 'en-US',
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  pl: 'pl-PL',
  uk: 'uk-UA',
  ru: 'ru-RU',
};

type SpeakButtonProps = Omit<ComponentPropsWithoutRef<typeof Button>, 'aria-label' | 'onClick' | 'type'> & {
  text: string;
  sound?: string | null;
  label?: string;
  lang?: string;
  rate?: number;
};

export function SpeakButton({
  text,
  sound,
  label,
  lang,
  rate = 0.9,
  disabled,
  ...props
}: SpeakButtonProps) {
  const { user } = useAuth();
  const speechLang = lang ?? SPEECH_LOCALES[user?.learningLanguage ?? ''] ?? 'en-US';
  const { isSpeaking, isSupported, speak } = useSpeechSynthesis({ lang: speechLang, rate });
  const trimmedText = text.trim();
  const isDisabled = disabled || !trimmedText || !isSupported;

  return (
    <Button
      {...props}
      type="button"
      aria-label={label ?? `Play pronunciation for ${trimmedText || 'this phrase'}`}
      aria-pressed={isSpeaking}
      disabled={isDisabled}
      onClick={() => speak(trimmedText, sound)}
    >
      {isSpeaking ? <LoaderCircle className="size-4 animate-spin" /> : <Volume2 className="size-4" />}
    </Button>
  );
}
